import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/cn';

/**
 * Tooltip — the canonical lightweight Radix overlay on
 * `@radix-ui/react-tooltip`, themed onto the HofSync brand tokens. Like Select,
 * the bubble (`TooltipContent`) renders through `TooltipPrimitive.Portal` to
 * `document.body`; its utilities are therefore all `tw:`-prefixed so the global
 * `dist/styles.css` paints it (see CLAUDE.md "Scoping"). There is no native UA
 * chrome to reset — Radix renders a plain `<div role="tooltip">` — but because
 * preflight is excluded we still set the surface/border/shadow explicitly on the
 * brand `--popover` / `--border` tokens.
 *
 * Wrap the app once in `TooltipProvider` (shared delay/skip timers), then use
 * `Tooltip` + `TooltipTrigger` + `TooltipContent` per control.
 * `@radix-ui/react-tooltip` is a real `dependency` (the DS ships as source).
 */
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

export type TooltipProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
>;
export type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
>;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      // Portal content: utilities MUST be tw:-prefixed (mounts on document.body,
      // styled only by the global dist/styles.css).
      className={cn(
        'tw:z-50 tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-popover tw:px-3 tw:py-1.5 tw:text-xs tw:text-popover-foreground tw:shadow-md',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
